import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { checkUsername } from '../services/api';
import getErrorMessage from '../utils/errorHandler';
import logo from '../Logos/LogoNoText cropped.png';
import '../styles/app/auth.css';
import '../styles/app/forms.css';

function Register() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, error: null });
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // Debounced username check
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus({ checking: false, available: null, error: username.length > 0 ? 'Username must be at least 3 characters' : null });
      return;
    }

    setUsernameStatus({ checking: true, available: null, error: null });
    try {
      const response = await checkUsername(username);
      setUsernameStatus({
        checking: false,
        available: response.data.available,
        error: response.data.available ? null : 'Username already taken'
      });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, error: 'Could not check username' });
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsernameAvailability(formData.username);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username, checkUsernameAvailability]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!acceptedTerms) {
      setError('Please accept the terms to continue');
      return;
    }

    if (usernameStatus.available === false || usernameStatus.error) {
      setError('Please choose a different username');
      return;
    }

    setLoading(true);

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });
      navigate(redirectUrl);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const usernameClass = usernameStatus.available === true ? ' is-valid' :
    usernameStatus.error ? ' is-invalid' : '';

  return (
    <div className="pnp-auth">
      <div className="pnp-auth-stage" aria-hidden="true">
        <span className="pnp-auth-beam" />
        <span className="pnp-auth-orb pnp-auth-orb-red" />
        <span className="pnp-auth-orb pnp-auth-orb-blue" />
        <svg className="pnp-auth-cage pnp-auth-cage-1" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <svg className="pnp-auth-cage pnp-auth-cage-2" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <svg className="pnp-auth-cage pnp-auth-cage-3" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <span className="pnp-claw pnp-auth-claw" />
      </div>

      <div className="pnp-auth-grid pnp-auth-grid-tall">
        <header className="pnp-auth-brand pnp-rise">
          <div className="pnp-auth-logo-wrap">
            <img className="pnp-auth-logo" src={logo} alt="Pick 'N Pray" />
          </div>
          <p className="pnp-eyebrow pnp-auth-eyebrow">Create account</p>
          <h1 className="pnp-auth-title">
            Join the <span className="pnp-auth-break pnp-punch">fight</span>
          </h1>
          <p className="pnp-auth-lede">Join Pick 'N Pray today. Free pick'em pools for fight night with your crew.</p>
          <ul className="pnp-auth-points">
            <li>Start a private pool or join one with a code</li>
            <li>Pick winners, methods and title fights</li>
            <li>Earn badges and climb the leaderboard</li>
          </ul>
        </header>

        <section className="pnp-auth-card pnp-rise" style={{ '--i': 1 }}>
          <div className="pnp-auth-card-head">
            <p className="pnp-eyebrow pnp-eyebrow-mute">New fighter</p>
            <h2 className="pnp-auth-card-title">Create Account</h2>
          </div>

          {error && (
            <div className="message message-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="pnp-auth-row">
              <div className="form-group">
                <label className="form-label" htmlFor="reg-first">First Name</label>
                <input
                  id="reg-first"
                  type="text"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="reg-last">Last Name</label>
                <input
                  id="reg-last"
                  type="text"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-username">Username</label>
              <div className="pnp-auth-field">
                <input
                  id="reg-username"
                  type="text"
                  name="username"
                  className={`form-input${usernameClass}`}
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Choose a username"
                  autoComplete="username"
                  autoCapitalize="none"
                  required
                />
                {formData.username && (
                  usernameStatus.checking ? (
                    <span className="pnp-auth-status is-checking" role="status" aria-label="Checking username" />
                  ) : usernameStatus.available === true ? (
                    <span className="pnp-auth-status is-ok" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                  ) : usernameStatus.error ? (
                    <span className="pnp-auth-status is-bad" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </span>
                  ) : null
                )}
              </div>
              {usernameStatus.error && (
                <span className="pnp-auth-hint pnp-auth-hint-bad">
                  {usernameStatus.error}
                </span>
              )}
              {usernameStatus.available === true && (
                <span className="pnp-auth-hint pnp-auth-hint-ok">
                  Username available
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-email">Email</label>
              <input
                id="reg-email"
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-password">Password</label>
              <input
                id="reg-password"
                type="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-confirm">Confirm Password</label>
              <input
                id="reg-confirm"
                type="password"
                name="confirmPassword"
                className="form-input"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                autoComplete="new-password"
                required
              />
            </div>

            <div className="pnp-auth-terms">
              <label className="pnp-form-check">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span className="pnp-form-check-label">
                  I am at least 18 years old and I agree to the{' '}
                  <Link to="/terms" target="_blank">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank">Privacy Policy</Link>.
                  I understand that Pick 'N Pray is for entertainment purposes only within private groups,
                  does not facilitate gambling, and I am solely responsible for compliance with applicable laws in my jurisdiction.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block pnp-auth-submit"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
              {!loading && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </form>

          <p className="pnp-auth-switch">
            Already have an account?{' '}
            <Link to={redirectUrl !== '/dashboard' ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'}>
              Sign in
            </Link>
          </p>
        </section>
      </div>
    </div>
  );
}

export default Register;
